import { DataCard } from '@fluentsms/agentnet-web-components';
import { Box, createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { Document } from '@contentful/rich-text-types';
import { MarketplaceProduct } from 'api/admin/types';
import { RenderContent } from 'features/help/components/RenderContent/RenderContent';
import { ContentfulJsonDocument } from 'features/help/types/contentfulJsonDocument';
import { FraudToolDetails } from 'features/help/types/fraudToolDetails';
import { Message } from 'features/help/types/messagingCollection';
import { FC } from 'react';

const emptyRichTextLinks: ContentfulJsonDocument['links'] = {
  assets: { hyperlink: [], block: [] },
  entries: { hyperlink: [] },
};

const toRichTextDocument = (richText?: { json: Document } | null): ContentfulJsonDocument | null => {
  if (!richText?.json) {
    return null;
  }

  return {
    json: richText.json,
    links: emptyRichTextLinks,
  };
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    verticalSpacing: {
      marginBottom: theme.spacing(3),
    },
    linkText: {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      display: 'block',
      marginTop: theme.spacing(1),
      '&:hover': {
        color: theme.palette.secondary.main,
      },
    },
    dataCard: {
      height: 'fit-content',
    },
    underwriterPrice: {
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(1),
    },
  }),
);

export interface FeatureDetailsAboutCardProps {
  selectedService: MarketplaceProduct;
  descriptionMessage?: Message | null;
  fraudToolDetails?: FraudToolDetails | null;
  isWireValidation: boolean;
  rapidResponsePdfUrl: string | null;
  onContactUsClick: () => void;
}

export const FeatureDetailsAboutCard: FC<FeatureDetailsAboutCardProps> = ({
  selectedService,
  descriptionMessage,
  fraudToolDetails,
  isWireValidation,
  rapidResponsePdfUrl,
  onContactUsClick,
}) => {
  const classes = useStyles();
  const { category } = selectedService;

  const renderMainContent = () => {
    if (fraudToolDetails) {
      const detailsDocument = toRichTextDocument(fraudToolDetails.details);
      const underwriter1Document = toRichTextDocument(fraudToolDetails.underwriter1Details);
      const underwriter2Document = toRichTextDocument(fraudToolDetails.underwriter2Details);

      return (
        <>
          {detailsDocument ? (
            <div className={classes.verticalSpacing}>
              <RenderContent document={detailsDocument} />
            </div>
          ) : null}
          {fraudToolDetails.underwriter1 ? (
            <Box className={classes.verticalSpacing}>
              <Typography variant="h4" gutterBottom>
                {fraudToolDetails.underwriter1}
              </Typography>
              {fraudToolDetails.underwriter1Price ? (
                <Typography variant="body1" className={classes.underwriterPrice}>
                  {fraudToolDetails.underwriter1Price}
                </Typography>
              ) : null}
              {underwriter1Document ? <RenderContent document={underwriter1Document} /> : null}
            </Box>
          ) : null}
          {fraudToolDetails.underwriter2 ? (
            <Box className={classes.verticalSpacing}>
              <Typography variant="h4" gutterBottom>
                {fraudToolDetails.underwriter2}
              </Typography>
              {fraudToolDetails.underwriter2Price ? (
                <Typography variant="body1" className={classes.underwriterPrice}>
                  {fraudToolDetails.underwriter2Price}
                </Typography>
              ) : null}
              {underwriter2Document ? <RenderContent document={underwriter2Document} /> : null}
            </Box>
          ) : null}
        </>
      );
    }

    if (descriptionMessage) {
      return (
        <div className={classes.verticalSpacing}>
          <RenderContent document={descriptionMessage.messageText} />
        </div>
      );
    }

    return (
      <Typography variant="body1">
        First American Title agents can now seamlessly share data between AgentNet® Production and AgentNet® Services,
        streamlining the order and delivery of production services.
      </Typography>
    );
  };

  return (
    <DataCard heading="Details" className={classes.dataCard}>
      <Box p={3}>
        <Grid container spacing={3}>
          <Grid item sm={9}>
            {renderMainContent()}
          </Grid>
          <Grid item sm={3}>
            <Box mb={2}>
              <Typography variant="h4" gutterBottom>
                Support
              </Typography>
              <Typography
                variant="body1"
                onClick={onContactUsClick}
                className={classes.linkText}
                data-testid="add-ons-link-agentnet-customer-support"
              >
                AgentNet<sup>&reg;</sup> Customer Support
              </Typography>
            </Box>

            {isWireValidation && rapidResponsePdfUrl && (
              <Box mb={2}>
                <Typography
                  variant="body1"
                  component="a"
                  href={rapidResponsePdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={classes.linkText}
                  data-testid="add-ons-link-fraud-rapid-response-protocol"
                >
                  Fraud Rapid Response Protocol
                </Typography>
              </Box>
            )}

            <Box mb={2}>
              <Typography variant="h4" gutterBottom>
                Categories
              </Typography>
              <Typography variant="body1">{category}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="h4" gutterBottom>
                Legal
              </Typography>
              <Typography
                variant="body1"
                component="a"
                href="https://www.firstam.com/privacy-policy/"
                target="_blank"
                rel="noreferrer"
                className={classes.linkText}
                data-testid="add-ons-link-privacy-policy"
              >
                Privacy Policy
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </DataCard>
  );
};
